import { type PartialRefundOptions, useTransactionPartialRefund } from "api/clients/useTransactionPartialRefund";
import { basisPointsFromDollars } from "@vyne-shared/utils/currency";
import type { FormInstance } from "antd";
import type { ManualPartialRefundRequest } from "api/requests";
import type { RefundModalEvent } from "./refundModal.events";

/**
 * Build a refund request to submit to the API, based on manual data entry.
 * @param refundForm The form containing user data to submit.
 * @returns Request data, or undefined if the form is not ready for submission.
 */
async function buildRequest(refundForm: FormInstance<ManualPartialRefundRequest>) {
    // Perform validation first - this may throw an error if fields are invalid, which is fine.
    let values: ManualPartialRefundRequest;
    try {
        // Perform validation first - this may throw an error if fields are invalid, which is fine.
        values = await refundForm.validateFields();
    } catch {
        // If validation fails, it will display error messages for the user, so we don't need to do anything else.
        return undefined;
    }

    return values;
}

/**
 * Hook to provision setting provider customization using data supplied by a form.
 * @param refundForm The form to obtain data from.
 * @param options Options to configure the submission behavior.
 * @returns React Query mutation properties, but with a submit function instead of a mutate function.
 */
export function usePartialRefundSubmission(
    patientId: number,
    refundForm: FormInstance<ManualPartialRefundRequest>,
    dispatch: React.Dispatch<RefundModalEvent>,
    options: Omit<PartialRefundOptions, "mutationFn">,
) {
    const { mutate, isPending, data, error, reset } = useTransactionPartialRefund(patientId, options);

    const submit = async (transactionId: number) => {
        const request = await buildRequest(refundForm);
        if (request) {
            dispatch({ type: "confirm-refund" });

            mutate({
                transactionId,
                amount: basisPointsFromDollars(request.amount),
            });
        }
    };

    return { isPending, data, submit, error, reset } as const;
}
